import { AvailabilityMap } from '@/contexts/item-context-types';
import { gql } from '@apollo/client';
import {
  ItemsContext_VariantsPackFieldsFragment,
  Substitutions_GetSubstitutionOptionsQuery,
  Substitutions_GetSubstitutionOptionsQueryVariables,
} from '@/types';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useCallback } from 'react';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useUserContext } from '@/contexts/user-context';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

export const GET_SUBSTITUTION_OPTIONS_QUERY = gql`
  query Substitutions_GetSubstitutionOptions(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
    $fulfillmentDate: String!
    $variantPackUUID: uuid!
  ) {
    getItemSubstitutions(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      fulfillment_date: $fulfillmentDate
      variant_pack_id: $variantPackUUID
    ) {
      available_quantity
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }
  }
  ${VARIANTS_PACK_FRAGMENT}
`;

interface GetSubstitutionArguments {
  fulfillmentDate: string;
  variantPacksInOrder: Set<string>;
  variantPackUUID: string;
}

export const useGetSubstitutionOptions = () => {
  const itemQueryVariables = useItemQueryVariables();
  const { selectedRestaurantUUID, selectedRestaurantRelationship } =
    useUserContext();
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();

  const getSubstitutionOptions = useLazyQuery<
    Substitutions_GetSubstitutionOptionsQuery,
    Substitutions_GetSubstitutionOptionsQueryVariables
  >(GET_SUBSTITUTION_OPTIONS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const getSubstitutionListOptions = useCallback(
    async ({
      fulfillmentDate,
      variantPacksInOrder,
      variantPackUUID,
    }: GetSubstitutionArguments) => {
      const { data } = await getSubstitutionOptions({
        variables: {
          ...itemQueryVariables,
          fulfillmentDate,
          restaurantUUID: selectedRestaurantUUID,
          supplierUUID: selectedRestaurantRelationship.supplierUUID,
          variantPackUUID,
        },
      });

      const availabilityMap: AvailabilityMap = {};
      const recommendedVariantPacks = (data?.getItemSubstitutions || []).reduce<
        ItemsContext_VariantsPackFieldsFragment[]
      >((acc, subOption) => {
        const variantPack = subOption.variant_pack;
        if (variantPack && !variantPacksInOrder.has(variantPack.uuid)) {
          acc.push(variantPack);

          if (typeof subOption.available_quantity === 'number') {
            availabilityMap[variantPack.uuid] = subOption.available_quantity;
          }
        }
        return acc;
      }, []);

      const substitutionListOptions = adaptAndGroupVariantPacks(
        recommendedVariantPacks,
      );
      return { availabilityMap, substitutionListOptions };
    },
    [
      adaptAndGroupVariantPacks,
      getSubstitutionOptions,
      itemQueryVariables,
      selectedRestaurantRelationship.supplierUUID,
      selectedRestaurantUUID,
    ],
  );

  return getSubstitutionListOptions;
};
